import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

// --- Animated Counter ---
const useCountUp = (end: number, duration: number, isVisible: boolean, suffix = '', prefix = '') => {
    const [count, setCount] = useState(0);
    const frameRef = useRef<number>();

    useEffect(() => {
        if (!isVisible) return;
        const startTime = performance.now();
        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * end));
            if (progress < 1) {
                frameRef.current = requestAnimationFrame(animate);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
    }, [end, duration, isVisible]);

    return prefix + count + suffix;
};

interface StatItem {
    title: string;
    value: number;
    suffix: string;
    description: string;
    color: string;
    isSpecial?: boolean;
    specialDisplay?: string;
}

const stats: StatItem[] = [
    {
        title: 'Alunos Formados em SFS',
        value: 200,
        suffix: '+',
        description: 'Navegadores habilitados pela JL na comunidade de São Francisco do Sul.',
        color: 'hsl(43, 74%, 53%)',
    },
    {
        title: 'Embarcações de Pesca Regularizadas',
        value: 95,
        suffix: '+',
        description: 'Pescadores com documentação em dia graças ao despachante especializado.',
        color: '#3b82f6',
    },
    {
        title: 'Satisfação dos Alunos',
        value: 98,
        suffix: '%',
        description: 'Avaliação dos alunos que passaram pela unidade de São Chico.',
        color: '#22c55e',
    },
    {
        title: 'Baía Navegável',
        value: 130,
        suffix: ' km²',
        description: 'A Baía da Babitonga, a maior de SC, é o nosso campo de treinamento.',
        color: '#a855f7',
    },
];

const sparkles = [
    { x: '8%', y: '20%', size: 3, delay: '0s', dur: '2.8s' },
    { x: '65%', y: '12%', size: 2, delay: '0.7s', dur: '3.2s' },
    { x: '85%', y: '55%', size: 4, delay: '1.2s', dur: '2.5s' },
    { x: '30%', y: '75%', size: 2, delay: '0.4s', dur: '3.5s' },
    { x: '50%', y: '90%', size: 3, delay: '1.0s', dur: '2.9s' },
];

const NumerosSFSSection: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="py-24 sm:py-32 bg-primary relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                    backgroundSize: '32px 32px',
                }}
            />
            <div
                className="absolute -top-32 left-1/4 w-[400px] h-[400px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(43 74% 53% / 0.1), transparent 65%)',
                    filter: 'blur(60px)',
                }}
            />
            <div
                className="absolute -bottom-32 right-1/4 w-[400px] h-[400px] pointer-events-none animate-blob"
                style={{
                    background: 'radial-gradient(circle, hsl(200 70% 50% / 0.08), transparent 65%)',
                    filter: 'blur(50px)',
                    animationDelay: '5s',
                }}
            />
            {sparkles.map((p, i) => (
                <div
                    key={i}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        left: p.x, top: p.y, width: p.size, height: p.size,
                        background: 'hsl(var(--gold))',
                        boxShadow: '0 0 6px 2px hsl(var(--gold) / 0.7)',
                        animation: `twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
                    }}
                />
            ))}

            <div className="container mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/20 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Resultados
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground">
                        A JL em São Chico em Números
                    </h2>
                    <p className="mt-4 text-lg text-primary-foreground/70 max-w-3xl mx-auto">
                        De pescadores a veranistas, nossa unidade SFS atende toda a comunidade náutica.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                    {stats.map((stat, i) => {
                        const displayValue = useCountUp(stat.value, 1.5, isVisible, stat.suffix);

                        return (
                            <motion.div
                                key={stat.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.5, delay: 0.2 + i * 0.12 }}
                                whileHover={{ scale: 1.05, y: -5 }}
                                className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-gold/40 transition-all duration-300 cursor-default"
                                style={{ boxShadow: '0 4px 20px hsl(0 0% 0% / 0.15)' }}
                            >
                                <p
                                    className="text-4xl sm:text-5xl font-bold mb-3"
                                    style={{
                                        color: stat.color,
                                        textShadow: isVisible ? `0 0 25px ${stat.color}66, 0 0 50px ${stat.color}33` : 'none',
                                    }}
                                >
                                    {displayValue}
                                </p>
                                <p className="text-sm font-semibold text-primary-foreground mb-1 uppercase tracking-wider">
                                    {stat.title}
                                </p>
                                <p className="text-xs text-primary-foreground/60 leading-relaxed">
                                    {stat.description}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default NumerosSFSSection;
